import React, { useEffect, useContext, useRef, useState } from 'react';
import FadeIn from 'react-fade-in';
import { toJS } from 'mobx';
import { Observer } from 'mobx-react-lite';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import AddressInput from '../../_shared/AddressInput';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import WorkOrderUpdateStore from '../../../../stores/WorkOrderUpdateStore';
import AuthStore from '../../../../stores/AuthStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as bh from '../../../../utilities/badgeHelper';
import * as sys from '../../../../utilities/systemHelper';

import api from '../../../../api';

import './UpdateShipping.scss';

function UpdateShipping(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const focusTimer = useRef(null);
    const shippingAddressRef = useRef(null);
    const previousAddresses = useRef(null);
    const workOrderUpdate = useContext(WorkOrderUpdateStore);
    const auth = useContext(AuthStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [shipToName, setShipToName] = useState(null);
    const [shippingAddress, setShippingAddress] = useState(null);
    const [displayOtherAddresses, setDisplayOtherAddresses] = useState(false);
    const [otherAddresses, setOtherAddresses] = useState([]);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        api.Purchases.shippingAddresses({
            parameters: [{
                field: 'CustomerId',
                value: workOrderUpdate.data.customerId,
            }, {
                field: 'ShippingAddressId',
                operator: '!=',
                value: null,
            }, {
                field: 'DeactivatedDateUtc',
                value: null,
            }],
            sortByFields: [{
                field: 'CreatedDateUtc',
                direction: 'DESC',
            }],
            includeTotalCount: false,
        })
            .then(({ data }) => {
                if (isMounted.current) {
                    const PreviousShippingAddresses = data.result && data.result.length > 0 ? data.result : [];
                    previousAddresses.current = PreviousShippingAddresses;
                }
            })
            .finally(() => {
                setIsReady(true);
            })

        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100)

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isReady) {
            if (workOrderUpdate.data) {
                const ShippingAddress = workOrderUpdate.data.purchase && workOrderUpdate.data.purchase.shippingAddress ? toJS(workOrderUpdate.data.purchase.shippingAddress) : null;

                setShipToName(workOrderUpdate.data.purchase.shipToName);
                setShippingAddress(ShippingAddress);

                setTimeout(() => {
                    const OtherAddresses = [];

                    if (workOrderUpdate.data.customer) {
                        let homeAddress, shippingAddress;

                        if (workOrderUpdate.data.customer.address) {
                            const previousHomeAddress = previousAddresses.current && previousAddresses.current.length > 0 ? previousAddresses.current.find(a => a.address.fullLine === workOrderUpdate.data.customer.address.fullLine) : null;

                            homeAddress = {
                                icon: 'fal fa-house',
                                address: previousHomeAddress ? previousHomeAddress.address : toJS(workOrderUpdate.data.customer.address),
                                lastUsed: previousHomeAddress ? sys.getFormattedShortDate(moment.utc(previousHomeAddress.lastPurchaseDateUtc).local()) : 'Never',
                                isDefault: false,
                            };
                        }
                        if (workOrderUpdate.data.customer.shippingAddress) {
                            const previousShippingAddress = previousAddresses.current && previousAddresses.current.length > 0 ? previousAddresses.current.find(a => a.address.fullLine === workOrderUpdate.data.customer.shippingAddress.fullLine) : null;

                            shippingAddress = {
                                icon: 'fal fa-truck',
                                address: previousShippingAddress ? previousShippingAddress.address : toJS(workOrderUpdate.data.customer.shippingAddress),
                                lastUsed: previousShippingAddress ? sys.getFormattedShortDate(moment.utc(previousShippingAddress.lastPurchaseDateUtc).local()) : 'Never',
                                isDefault: true,
                            };
                        }

                        if (homeAddress) {
                            homeAddress.isDefault = !!shippingAddress && homeAddress.address.fullLine === shippingAddress.address.fullLine;
                            OtherAddresses.push(homeAddress);
                        }

                        if (shippingAddress && (!homeAddress || homeAddress.address.fullLine !== shippingAddress.address.fullLine)) {
                            OtherAddresses.push(shippingAddress);
                        }
                    }

                    for (let ai = 0; ai < previousAddresses.current.length; ai++) {
                        if (!OtherAddresses.some(a => a.address.fullLine === previousAddresses.current[ai].address.fullLine)) {
                            OtherAddresses.push({
                                icon: 'fal fa-truck',
                                address: previousAddresses.current[ai].address,
                                lastUsed: sys.getFormattedShortDate(moment.utc(previousAddresses.current[ai].lastPurchaseDateUtc).local()),
                                isDefault: false,
                            })
                        }
                    }

                    if (OtherAddresses && OtherAddresses.length > 0) {
                        setOtherAddresses(OtherAddresses);
                    }

                    if (ShippingAddress) {
                        shippingAddressRef.current.setAddress(ShippingAddress);
                    }
                }, 0);
            }
        }
    }, [isReady]) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (workOrderUpdate.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleShipToNameChange = (event) => {
        const { value } = event.target;
        setShipToName(value);
        workOrderUpdate.hasUnsavedChanges = true;
    }

    const handleShippingAddressChange = (address) => {
        setShippingAddress(address);
        workOrderUpdate.hasUnsavedChanges = true;
    }

    const handleClear = () => {
        setShipToName(null);
        setShippingAddress(null);
        shippingAddressRef.current.handleClearAddress();
        workOrderUpdate.hasUnsavedChanges = true;
    }

    const handleCopyAddress = (event, address) => {
        setShippingAddress(address);
        shippingAddressRef.current.setAddress(address);
        workOrderUpdate.hasUnsavedChanges = true;
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (shippingAddressRef.current.validate()) {
            if (workOrderUpdate.hasUnsavedChanges) {
                const updated = workOrderUpdate.hasUnsavedChanges;

                workOrderUpdate.updateShippingInformation(shipToName, shippingAddress)
                    .then(data => {
                        if (isMounted.current) {
                            if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                                props.onSuccess(event, { updated: updated, data: data });
                            }
                        }
                    })
            } else {
                if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                    props.onSuccess(event, { updated: false, data: null });
                }
            }
        }
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <fieldset disabled={workOrderUpdate.isSaving}>
                    <div className='update-shipping-information-container quick-drawer'>
                        <QuickDrawerHeader
                            drawer={props.drawer}
                            icon={'fal fa-truck'}
                            action='Update'
                            category='Shipping Information'
                            className='orders'
                            onCancel={handleCancel}
                        />
                        <div className='quick-drawer-body'>
                            {
                                isReady && workOrderUpdate.isReady && workOrderUpdate.data ?
                                    <FadeIn>
                                        <div className='body-content'>
                                            <section className='mb-4'>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <Observer>{() => <>
                                                            <div className='d-flex flex-row'>
                                                                <div className='fs-md fw-500 d-block text-gray-700 text-truncate text-truncate-lg'>
                                                                    {
                                                                        workOrderUpdate.data.items && workOrderUpdate.data.items[0] && workOrderUpdate.data.items[0].transactionItems && workOrderUpdate.data.items[0].transactionItems.length > 1 ?
                                                                            <span className='mr-1'>{workOrderUpdate.data.items[0].transactionItems.length}x</span> : null
                                                                    }
                                                                    {workOrderUpdate.data.items[0] ? workOrderUpdate.data.items[0].descriptor : ''}
                                                                </div>
                                                                <div className='ml-auto'>
                                                                    {bh.renderWorkOrderRedo(workOrderUpdate.data, 'p-1 mr-1')}{bh.renderWorkOrderStatus(workOrderUpdate.data, 'p-1')}
                                                                </div>
                                                            </div>
                                                            {
                                                                workOrderUpdate.data.items.length > 1 ?
                                                                    workOrderUpdate.data.items.slice(1).map((i, ii) => {
                                                                        return <div
                                                                            key={`order-${ii}`}
                                                                            className='fs-xs d-flex'>
                                                                            {
                                                                                i.transactionItems && i.transactionItems.length > 1 ?
                                                                                    <div
                                                                                        className='mr-1'
                                                                                    >
                                                                                        {i.transactionItems.length}x
                                                                                    </div> : null
                                                                            }
                                                                            {i.descriptor}
                                                                        </div>
                                                                    }) : null
                                                            }
                                                        </>}</Observer>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-2'>
                                                            <label htmlFor='ship-to-name'><small>Recipient name</small></label>
                                                            <Observer>{() =>
                                                                <input
                                                                    id='ship-to-name'
                                                                    type='text'
                                                                    className='form-control'
                                                                    value={shipToName ? shipToName : ''}
                                                                    onChange={handleShipToNameChange}
                                                                />
                                                            }</Observer>
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                            <section>
                                                <Observer>{() =>
                                                    <>
                                                        <AddressInput
                                                            ref={shippingAddressRef}
                                                            label={'Shipping address'}
                                                            defaultCountryCode={(auth.currentUser && auth.currentTenant && auth.currentTenant.address ? auth.currentTenant.address.countryCode : null)}
                                                            onChange={handleShippingAddressChange}
                                                        />
                                                    </>
                                                }</Observer>
                                            </section>
                                            {
                                                otherAddresses && otherAddresses.length > 0 ?
                                                    <>
                                                        <section>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group'>
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-link p-0 fw-500 mb-2'
                                                                            onClick={() => { setDisplayOtherAddresses(!displayOtherAddresses) }}
                                                                        >
                                                                            All addresses on file ({otherAddresses.length})
                                                                        </button>
                                                                        {
                                                                            displayOtherAddresses ?
                                                                                <FadeIn>{
                                                                                    otherAddresses && otherAddresses.length > 0 ?
                                                                                        otherAddresses.map((m, mi) => {
                                                                                            return <button
                                                                                                key={`shipping-previous-address-${mi}`}
                                                                                                type='button'
                                                                                                className='btn btn-light w-100 text-left tt-normal fw-400 text-info-hover mb-2'
                                                                                                onClick={(e) => { handleCopyAddress(e, m.address) }}
                                                                                            >
                                                                                                <div className='d-flex align-items-center w-100'>
                                                                                                    <div className='py-2 pr-3'>
                                                                                                        <i className={`${m.icon} fs-150`}></i>
                                                                                                    </div>
                                                                                                    <div className='flex-1'>
                                                                                                        {
                                                                                                            m.address && m.address.addressLine1 ?
                                                                                                                <>
                                                                                                                    {
                                                                                                                        m.address.line2 ?
                                                                                                                            <div>{m.address.addressLine1}, {m.address.line2}</div> :
                                                                                                                            <div>{m.address.addressLine1}</div>
                                                                                                                    }
                                                                                                                </> : null

                                                                                                        }
                                                                                                        {
                                                                                                            m.address && m.address.addressLine2 ?
                                                                                                                <div>{m.address.addressLine2} {m.address.country}</div> : null
                                                                                                        }
                                                                                                        <div className='fs-sm text-gray-600 mt-1'>
                                                                                                            {
                                                                                                                m.isDefault ?
                                                                                                                    <><strong className='text-info'>Default shipping address</strong></> :
                                                                                                                    <><strong>Last used:</strong> {m.lastUsed}</>
                                                                                                            }
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </button>
                                                                                        }) : null
                                                                                }
                                                                                </FadeIn> : null
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </section>
                                                    </> : null
                                            }
                                        </div>
                                    </FadeIn> : renderQuickDrawerLoading()
                            }
                        </div>
                        <div className='quick-drawer-action pl-3'>
                            <div className='row'>
                                <div className='col-4'>
                                    <button
                                        type='button'
                                        className='btn btn-light'
                                        onClick={handleClear}
                                    >Clear</button>
                                </div>
                                <div className='col-8'>
                                    <div className='float-right'>
                                        <button
                                            type='button'
                                            className='btn btn-link btn-cancel mr-2'
                                            onClick={handleCancel}
                                        >Cancel</button>
                                        <button
                                            type='submit'
                                            className='btn btn-success'
                                        >Save</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            }</Observer>
        </form>
    </>
}

export default UpdateShipping;